import { MonitorService } from '@/api/services/MonitorService';
import { MonitorSearchCriteria, MonitoringSearchDtoV2 } from '@/api';
import store from '@/store';
import authModule from '@/store/modules/auth';
import { getModule } from 'vuex-module-decorators';
import callWithContext from '@/api/call-with-context';
import { ToolDefinition } from '../types';

const searchMonitorsTool: ToolDefinition = {
  name: 'search_monitors',
  description: 'Search monitors (rules, data quality checks). Use for counts, lists, details, status, criticality, coverage. Response: searchRules { data, overallElements, filteredElements }, catalogFilters. Fields: id, name, ruleStatus (PASSING, FAILING, NEEDS_ATTENTION, NOT_EVALUATED), criticality, datasets (asset UUIDs). Use dataset to filter by asset UUIDs. Use domain (name) to filter. Use sort for ordering. For custom metadata filters, use customMetadataValues with entry IDs (UUIDs). IMPORTANT: For filter breakdowns (e.g., counts by status), use catalogFilters.children[].results directly if available. Do NOT make additional filtered searches if results are provided in catalogFilters.',
  input_schema: {
    type: 'object',
    properties: {
      textSearch: {
        type: 'string',
        description: 'Match text in monitor names/descriptions',
      },
      ruleStatus: {
        type: 'array',
        description: 'Status: PASSING, FAILING, NEEDS_ATTENTION, NOT_EVALUATED',
        items: {
          type: 'string',
        },
      },
      criticality: {
        type: 'array',
        description: 'Criticality: CRITICAL, HIGH, MODERATE, LOW',
        items: {
          type: 'string',
        },
      },
      dataset: {
        type: 'array',
        description: 'Asset UUIDs or URNs (UUID extracted from URN if provided)',
        items: {
          type: 'string',
        },
      },
      tag: {
        type: 'array',
        description: 'Tag names or IDs',
        items: {
          type: 'string',
        },
      },
      term: {
        type: 'array',
        description: 'Term names or IDs',
        items: {
          type: 'string',
        },
      },
      customMetadataValues: {
        type: 'array',
        description: 'Custom metadata entry IDs',
        items: {
          type: 'string',
        },
      },
      ruleTemplateName: {
        type: 'array',
        description: 'Monitor template names',
        items: {
          type: 'string',
        },
      },
      domain: {
        type: 'string',
        description: 'Domain name (default: selected domain)',
      },
      page: {
        type: 'integer',
        description: 'Page number (default: 0)',
      },
      itemsPerPage: {
        type: 'integer',
        description: 'Items per page (default: 25)',
      },
      sort: {
        type: 'array',
        description: 'Sort: ["property,ASC"] or ["property,DESC"]. Examples: ["name,ASC"], ["ruleStatus,DESC"]',
        items: {
          type: 'string',
        },
      },
    },
    required: [],
  },
};

/**
 * Execute the search_monitors tool
 */
export async function executeSearchMonitors(
  params: any,
): Promise<MonitoringSearchDtoV2 | { error: string }> {
  try {
    // Get the auth module to access selected domain
    const auth = getModule(authModule, store);
    const selectedDomain = auth.selectedDomain?.name;

    // Transform params to MonitorSearchCriteria format
    const {
      page,
      itemsPerPage,
      sort,
      domain,
      ruleStatus,
      criticality,
      dataset,
      tag,
      term,
      customMetadataValues,
      ruleTemplateName,
      textSearch,
      ...otherParams
    } = params;

    // Extract UUID from dataset URNs if provided
    // URN format: "urn:sifflet:dataset:UUID" or "dataset:UUID" -> extract UUID
    // Also handle if already just UUID
    let processedDataset = dataset;
    if (dataset && Array.isArray(dataset)) {
      processedDataset = dataset.map((ds: string) => {
        // If it's already a UUID (format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx), return as-is
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ds)) {
          return ds;
        }
        // If it contains "dataset:", extract the UUID part after "dataset:"
        if (ds.includes('dataset:')) {
          const parts = ds.split('dataset:');
          return parts[parts.length - 1];
        }
        // If it's a URN format like "urn:sifflet:dataset:UUID", extract the last part
        if (ds.includes(':')) {
          const parts = ds.split(':');
          return parts[parts.length - 1];
        }
        // Otherwise return as-is (might be invalid, but let API handle validation)
        return ds;
      });
    }

    const requestBody: MonitorSearchCriteria = {
      page: page || 0,
      itemsPerPage: itemsPerPage || 25,
      sort: sort || ['name,ASC'],
      domain: domain || selectedDomain,
      ...(ruleStatus && { ruleStatus }),
      ...(criticality && { criticality }),
      ...(processedDataset && { dataset: processedDataset }),
      ...(tag && { tag }),
      ...(term && { term }),
      ...(customMetadataValues && { customMetadataEntry: customMetadataValues }),
      ...(ruleTemplateName && { ruleTemplateName }),
      ...(textSearch && { textSearch }),
      ...otherParams,
    };

    const result = await callWithContext('sifflet-ai-chat', () => MonitorService.getAllMonitor({
      requestBody,
    }));

    return result;
  } catch (error) {
    return { error: (error as Error).message };
  }
}

export default searchMonitorsTool;
